import React, { useEffect, useRef, useState } from 'react';
import { FaSave }      from '@react-icons/all-files/fa/FaSave';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaTimes }     from '@react-icons/all-files/fa/FaTimes';
import { FaSpinner }   from '@react-icons/all-files/fa/FaSpinner';

// ── Field descriptor ───────────────────────────────────────────────────────

export type FieldType = 'text' | 'textarea' | 'number' | 'email' | 'date' | 'select' | 'checkbox' | 'autocomplete' | 'autocomplete-on-demand' | 'file';

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface FormField<T extends Record<string, any> = Record<string, any>> {
  /** Key in the form values object */
  name: keyof T;
  label: string;
  type?: FieldType;
  placeholder?: string;
  required?: boolean;
  /** Span all columns */
  fullWidth?: boolean;
  /** Only shown in edit mode (isNew === false) */
  editOnly?: boolean;
  /** Never shown in the form (e.g. hidden id field handled externally) */
  hidden?: boolean;
  /** Rendered as read-only plain text */
  readOnly?: boolean;
  /** Deshabilita la edición del campo */
  disabled?: boolean;
  /** Texto auxiliar bajo la etiqueta */
  hint?: string;
  rows?: number;
  options?: SelectOption[];
  // ── autocomplete-on-demand specific ──────────────────────────────────────
  /** Base URL; query params are appended automatically */
  url?: string;
  /** Name of the query parameter sent to the server (default: 'query') */
  queryParam?: string;
  /** Key in each response object used as the option value (default: 'id') */
  valueKey?: string;
  /** Key in each response object used as the option label (default: 'nombre') */
  labelKey?: string;
  /** Minimum characters before the first request is fired (default: 2) */
  minChars?: number;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Additional fixed query params appended to every request */
  extraParams?: Record<string, string>;
  /** Resolves the display text for a selected value.
   *  `value` is the committed ID string; `raw` is the full server object (available
   *  after the user picks from the dropdown, undefined on initial edit-mode load). */
  labelResolver?: (value: string, raw?: any) => string | undefined;
  /** Custom renderer for each row in the dropdown.
   *  Receives the raw server item, the computed option, and whether it is currently selected. */
  renderOption?: (raw: any, opt: SelectOption, isSelected: boolean) => React.ReactNode;
  /** Called when the user selects or clears a file (type === 'file').
   *  Receives the raw File object, or null when the file is removed. */
  onFileSelect?: (file: File | null) => void;
}

// ── Autocomplete sub-component ─────────────────────────────────────────────

interface AutocompleteFieldProps {
  value: string;
  options: SelectOption[];
  placeholder?: string;
  hasError?: boolean;
  onChange: (value: string) => void;
}

function AutocompleteField({ value, options, placeholder, hasError, onChange }: AutocompleteFieldProps) {
  const selected      = options.find(o => String(o.value) === String(value));
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const [inputText, setInputText] = useState(selected?.label ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync displayed text when the external value changes (e.g. entity load)
  useEffect(() => {
    const opt = options.find(o => String(o.value) === String(value));
    setInputText(opt?.label ?? '');
    setQuery('');
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        // If the user abandoned typing without choosing, restore the selected label
        const opt = options.find(o => String(o.value) === String(value));
        setInputText(opt?.label ?? '');
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value, options]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setInputText(e.target.value);
    setOpen(true);
  };

  const handleSelect = (opt: SelectOption) => {
    onChange(String(opt.value));
    setInputText(opt.label);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    setQuery('');
    setOpen(false);
  };

  const inputBorder = hasError ? 'profase-form-input--error' : open ? 'profase-form-input--focused' : '';
  const inputClasses = ['profase-form-input', 'profase-form-input-with-clear', inputBorder].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className="profase-form-input-wrap">
        <input
          type="text"
          value={inputText}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className={inputClasses}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            title="Limpiar"
            className="profase-form-clear-btn"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <ul className="profase-form-dropdown">
          {filtered.map(opt => (
            <li
              key={opt.value}
              onMouseDown={() => handleSelect(opt)}
              className={
                String(opt.value) === String(value)
                  ? 'profase-form-dropdown-item profase-form-dropdown-item--selected'
                  : 'profase-form-dropdown-item'
              }
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {open && filtered.length === 0 && query.length > 0 && (
        <div className="profase-form-dropdown-empty">
          Sin resultados
        </div>
      )}
    </div>
  );
}

// ── Autocomplete-on-demand sub-component ───────────────────────────────────

/** Extended option that also carries the original server payload */
interface OnDemandOption extends SelectOption {
  raw: any;
}

interface AutocompleteOnDemandFieldProps {
  value:          string;
  url:            string;
  queryParam?:    string;
  valueKey?:      string;
  labelKey?:      string;
  minChars?:      number;
  debounceMs?:    number;
  placeholder?:   string;
  hasError?:      boolean;
  extraParams?:   Record<string, string>;
  labelResolver?: (value: string, raw?: any) => string | undefined;
  renderOption?:  (raw: any, opt: SelectOption, isSelected: boolean) => React.ReactNode;
  onChange:       (value: string) => void;
}

function AutocompleteOnDemandField({
  value,
  url,
  queryParam    = 'query',
  valueKey      = 'id',
  labelKey      = 'nombre',
  minChars      = 2,
  debounceMs    = 300,
  placeholder,
  hasError,
  extraParams,
  labelResolver,
  renderOption,
  onChange,
}: AutocompleteOnDemandFieldProps) {
  const [inputText, setInputText] = useState('');
  const [results,   setResults]   = useState<OnDemandOption[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [open,      setOpen]      = useState(false);

  const containerRef     = useRef<HTMLDivElement>(null);
  const debounceRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef         = useRef<AbortController | null>(null);
  const labelResolverRef = useRef(labelResolver);
  // Stores the raw server object of the last item chosen from the dropdown
  const selectedRawRef   = useRef<any>(undefined);

  // Keep refs fresh on every render
  useEffect(() => { labelResolverRef.current = labelResolver; });

  /** Resolves the display text for the committed value, using the raw item when available */
  const resolveLabel = (v: string) =>
    labelResolverRef.current?.(v, selectedRawRef.current) ?? selectedRawRef.current?.[labelKey] ?? v;

  // Sync display text when the selected value changes (e.g. entity loaded in edit mode)
  useEffect(() => {
    if (value) {
      const resolved = resolveLabel(value);
      setInputText(resolved);
    } else {
      setInputText('');
      selectedRawRef.current = undefined;
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click and restore the committed label
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setInputText(value ? resolveLabel(value) : '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async (q: string) => {
    if (q.length < minChars) { setResults([]); setOpen(false); return; }
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const params = new URLSearchParams({ [queryParam]: q });
    if (extraParams) Object.entries(extraParams).forEach(([k, v]) => params.append(k, v));
    setLoading(true);
    try {
      const res  = await fetch(`${url}?${params}`, { signal: abortRef.current.signal });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const arr: any[] = Array.isArray(data) ? data : (data.content ?? data.data ?? []);
      setResults(arr.map(item => ({
        value: String(item[valueKey]),
        label: String(item[labelKey] ?? item[valueKey]),
        raw:   item,
      })));
      setOpen(true);
    } catch (err: any) {
      if (err.name !== 'AbortError') setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setInputText(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), debounceMs);
  };

  const handleSelect = (opt: OnDemandOption) => {
    selectedRawRef.current = opt.raw;
    onChange(String(opt.value));
    setInputText(resolveLabel(String(opt.value)));
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    selectedRawRef.current = undefined;
    onChange('');
    setInputText('');
    setResults([]);
    setOpen(false);
  };

  const inputBorder = hasError ? 'profase-form-input--error' : open ? 'profase-form-input--focused' : '';
  const inputClasses = ['profase-form-input', 'profase-form-input-with-clear', inputBorder].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className="profase-form-input-wrap">
        <input
          type="text"
          value={inputText}
          placeholder={placeholder ?? `Buscar… (mín. ${minChars} car.)`}
          onChange={handleInputChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          className={inputClasses}
          autoComplete="off"
        />
        {loading && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--crm-text-muted)', display: 'flex', alignItems: 'center', pointerEvents: 'none',
          }}>
            <FaSpinner style={{ fontSize: 13, animation: 'spin 1s linear infinite' }} />
          </span>
        )}
        {!loading && value && (
          <button type="button" onClick={handleClear} title="Limpiar" className="profase-form-clear-btn">
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="profase-form-dropdown">
          {results.map(opt => (
            <li
              key={opt.value}
              onMouseDown={() => handleSelect(opt)}
              className={
                String(opt.value) === String(value)
                  ? 'profase-form-dropdown-item profase-form-dropdown-item--selected'
                  : 'profase-form-dropdown-item'
              }
            >
              {renderOption ? renderOption(opt.raw, opt, String(opt.value) === String(value)) : opt.label}
            </li>
          ))}
        </ul>
      )}

      {open && !loading && results.length === 0 && inputText.length >= minChars && (
        <div className="profase-form-dropdown-empty">
          Sin resultados
        </div>
      )}
    </div>
  );
}

// ── FileField sub-component ────────────────────────────────────────────────

interface FileFieldProps {
  /** Current filename (controlled — the string value from form state) */
  value: string;
  placeholder?: string;
  hasError?: boolean;
  onChange: (filename: string) => void;
  onFileSelect?: (file: File | null) => void;
  accept?: string;
}

function FileField({ value, placeholder, hasError, onChange, onFileSelect, accept }: FileFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      onChange(file.name);
      onFileSelect?.(file);
    }
    // reset so the same file can be re-selected after removal
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
    onFileSelect?.(null);
  };

  return (
    <div style={fs.root}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />

      {value ? (
        <div className={`profase-form-file-chip${hasError ? ' profase-form-file-chip--error' : ''}`}>
          <span className="profase-form-file-name" title={value}>{value}</span>
          <button
            type="button"
            className="profase-form-file-remove"
            onClick={handleRemove}
            title="Quitar archivo"
          >
            <FaTimes size={11} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={`profase-form-file-pick${hasError ? ' profase-form-file-pick--error' : ''}`}
          onClick={handlePick}
        >
          <span style={{ fontSize: 15 }}>📎</span>
          {placeholder ?? 'Seleccionar archivo…'}
        </button>
      )}
    </div>
  );
}

const fs: Record<string, React.CSSProperties> = {
  root: { display: 'flex', alignItems: 'center' },
};

// ── Component props ────────────────────────────────────────────────────────

export interface PageFormProps<T extends Record<string, any> = Record<string, any>> {
  fields: FormField<T>[];
  values: T;
  errors?: Partial<Record<keyof T, string>>;
  isNew?: boolean;
  saving?: boolean;
  onFieldChange: (name: keyof T, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  /** Accent color for the submit button (defaults to #6366f1) */
  accentColor?: string;
  submitLabel?: string;
  cancelLabel?: string;
  /** Contenido opcional entre Cancelar y el botón principal (ej. “Importar”) */
  actionSlotAfterCancel?: React.ReactNode;
  /** Oculta la barra de acciones (Cancelar / Guardar) del formulario */
  hideActions?: boolean;
  /** Contenido renderizado después de los campos y antes de las acciones */
  contentBeforeActions?: React.ReactNode;
  /** Id del elemento `<form>` (permite botones submit externos con atributo `form`) */
  formId?: string;
  /** Etiqueta a la izquierda y control a la derecha en la misma fila */
  labelInline?: boolean;
  /** Sin card propia cuando el formulario va dentro de un modal */
  embedded?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function PageForm<T extends Record<string, any>>({
  fields,
  values,
  errors = {},
  isNew = true,
  saving = false,
  onFieldChange,
  onSubmit,
  onCancel,
  accentColor = '#6366f1',
  submitLabel,
  cancelLabel = 'Cancelar',
  actionSlotAfterCancel,
  hideActions = false,
  contentBeforeActions,
  formId,
  labelInline = false,
  embedded = false,
}: PageFormProps<T>) {
  const visibleFields = fields.filter(f => {
    if (f.hidden) return false;
    if (f.editOnly && isNew) return false;
    return true;
  });

  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      className={`profase-page-form${embedded ? ' profase-page-form--embedded' : ''}`}
    >
      <div style={s.grid}>
        {visibleFields.map((field, idx) => {
          const error   = errors[field.name] as string | undefined;
          const value   = (values[field.name] ?? '') as string;
          const inputClass = [
            field.type === 'textarea' ? 'profase-form-textarea' : field.type === 'select' ? 'profase-form-select' : 'profase-form-input',
            error ? `${field.type === 'textarea' ? 'profase-form-textarea' : field.type === 'select' ? 'profase-form-select' : 'profase-form-input'}--error` : '',
            field.readOnly ? `${field.type === 'textarea' ? 'profase-form-textarea' : field.type === 'select' ? 'profase-form-select' : 'profase-form-input'}--readonly` : '',
            field.disabled ? `${field.type === 'textarea' ? 'profase-form-textarea' : field.type === 'select' ? 'profase-form-select' : 'profase-form-input'}--disabled` : '',
          ].filter(Boolean).join(' ');
          const colSpan: React.CSSProperties = {};

          const isCheckbox = field.type === 'checkbox';
          const checked    = value === 'true' || value === true as any;
          const inlineRow  = labelInline && !isCheckbox;

          const inputBody = !isCheckbox ? (
                <>
                  {field.readOnly ? (
                    <div className="profase-form-readonly-value">{value || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}</div>

                  ) : field.type === 'textarea' ? (
                    <textarea
                      className={inputClass}
                      style={{ height: (field.rows ?? 4) * 24, resize: 'vertical' }}
                      value={value}
                      placeholder={field.placeholder}
                      disabled={field.disabled}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    />

                  ) : field.type === 'select' ? (
                    <select
                      className={inputClass}
                      value={value}
                      disabled={field.disabled}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    >
                      <option value="">— Selecciona —</option>
                      {(field.options ?? []).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>

                  ) : field.type === 'autocomplete' ? (
                    <AutocompleteField
                      value={value}
                      options={field.options ?? []}
                      placeholder={field.placeholder ?? 'Buscar…'}
                      hasError={!!error}
                      onChange={v => onFieldChange(field.name, v)}
                    />

                  ) : field.type === 'autocomplete-on-demand' ? (
                    <AutocompleteOnDemandField
                      value={value}
                      url={field.url ?? ''}
                      queryParam={field.queryParam}
                      valueKey={field.valueKey}
                      labelKey={field.labelKey}
                      minChars={field.minChars}
                      debounceMs={field.debounceMs}
                      placeholder={field.placeholder}
                      hasError={!!error}
                      extraParams={field.extraParams}
                      labelResolver={field.labelResolver}
                      renderOption={field.renderOption}
                      onChange={v => onFieldChange(field.name, v)}
                    />

                  ) : field.type === 'file' ? (
                    <FileField
                      value={value}
                      placeholder={field.placeholder}
                      hasError={!!error}
                      onChange={v => onFieldChange(field.name, v)}
                      onFileSelect={field.onFileSelect}
                    />

                  ) : (
                    <input
                      className={inputClass}
                      type={field.type ?? 'text'}
                      value={value}
                      placeholder={field.placeholder}
                      autoFocus={idx === 0}
                      disabled={field.disabled}
                      onChange={e => onFieldChange(field.name, e.target.value)}
                    />
                  )}
                </>
          ) : null;

          return (
            <div
              key={String(field.name)}
              style={{
                ...s.fieldGroup,
                ...(inlineRow ? s.fieldGroupInline : {}),
                ...colSpan,
              }}
            >

              {isCheckbox ? (
                <>
                  <label style={s.checkRow}>
                    <input
                      type="checkbox"
                      style={s.checkInput}
                      checked={checked}
                      onChange={e => onFieldChange(field.name, String(e.target.checked))}
                    />
                    <span className="profase-form-label">
                      {field.label}
                      {field.required && <span className="profase-form-required"> *</span>}
                    </span>
                  </label>
                  {error && <span className="profase-form-error-msg">{error}</span>}
                </>
              ) : inlineRow ? (
                <>
                  <label className="profase-form-label" style={s.labelInlineCell}>
                    {field.label}
                    {field.required && <span className="profase-form-required"> *</span>}
                  </label>
                  <div style={s.fieldControlWrap}>
                    {inputBody}
                    {field.hint && (
                      <span className="profase-form-hint">{field.hint}</span>
                    )}
                    {error && <span className="profase-form-error-msg">{error}</span>}
                  </div>
                </>
              ) : (
                <>
                  <label className="profase-form-label">
                    {field.label}
                    {field.required && <span className="profase-form-required"> *</span>}
                  </label>

                  {inputBody}
                  {field.hint && (
                    <span className="profase-form-hint">{field.hint}</span>
                  )}
                  {error && <span className="profase-form-error-msg">{error}</span>}
                </>
              )}
            </div>
          );
        })}
      </div>

      {contentBeforeActions}

      {!hideActions ? (
        <div className="profase-form-actions">
          <button type="button" className="profase-form-btn-secondary" onClick={onCancel} disabled={saving}>
            <FaArrowLeft style={{ marginRight: 6 }} />
            {cancelLabel}
          </button>
          {actionSlotAfterCancel}
          <button type="submit" className="profase-form-btn-primary" style={{ background: accentColor }} disabled={saving}>
            <FaSave style={{ marginRight: 6 }} />
            {saving ? 'Guardando…' : (submitLabel ?? 'Guardar')}
          </button>
        </div>
      ) : null}
    </form>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px 0',
  },
  fieldGroup:    { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldGroupInline: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  } as React.CSSProperties,
  labelInlineCell: {
    flexShrink: 0,
    minWidth: 132,
    maxWidth: '42%',
    paddingTop: 9,
    lineHeight: 1.35,
  } as React.CSSProperties,
  fieldControlWrap: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  } as React.CSSProperties,
  label:         { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' } as React.CSSProperties,
  checkRow:      { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' } as React.CSSProperties,
  checkInput:    { width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer', flexShrink: 0 } as React.CSSProperties,
};
